/* eslint-disable max-len */
import StringifyObject from 'stringify-object';
import NavigationData from './navigationData';
import Constants from '../constants';
import Utils from '../utils';

export default class NavigationTracking {
  event = {
    id: {},
    context: {},
    data: {},
  };
  maxPartialLength = 20;
  timeTick = 10000;
  version = 14;

  constructor(core) {
    this.sessionInfo = core.sessionInfo;
    this.core = core;
    this.event.id = {
      customerId: this.core.customerId,
      userIdentifier: this.core.userId,
      day: Date.now(),
      orderId: 0,
    };

    this.event.email = this.sessionInfo?.context?.customer?.email;
    this.event.custom = this.sessionInfo?.context?.custom;
    this.event.geoloc = this.sessionInfo?.context?.geoloc;
    this.event.context = this.sessionInfo?.context;
    this.load();
    setInterval(this.pageTimeTick, this.timeTick);
  }

  save = () => {
    if (Utils.isJsonStringify(this.event)) {
      localStorage.setItem('cib_postback', btoa(encodeURI(JSON.stringify(this.event))));
    } else {
      try {
        if (Math.random() * 100 > 95) {
          Utils.message('stringify error: save', StringifyObject(this.event));
        }
      } catch (e) {
        Utils.message('stringify error: save');
        console.log(e);
      }
    }
  };

  load = () => {
    let dataString = localStorage.getItem('cib_postback');
    if (dataString) {
      dataString = JSON.parse(decodeURI(atob(dataString)));
    }
    this.event.data = new NavigationData(dataString);
    this.loadSession();
  };

  onPageView = () => {
    if (this.sessionInfo.context.categories && typeof (this.sessionInfo.context.categories) === 'string') {
      this.updatePartialMetrics(this.sessionInfo.context.categories, 'pageView');
    }
    this.event.data.session.pageView += 1;
    this.event.data.total.pageView += 1;
    this.event.data.daily.pageView += 1;

    this.onEvent('pageView');
    this.collect();
  };

  onClick = () => {
    this.onEvent('click');
    this.collect(true);
  };

  onProductPageView = () => {
    if (this.sessionInfo.context.product && this.sessionInfo.context.product.price) {
      this.event.data.session.productView += 1;
      this.event.data.daily.productView += 1;
      this.event.data.total.productView += 1;
    }
    this.save();
  };

  onExit = () => {
    this.computeTimeValues();
    this.onEvent('exit');
    this.save();
    this.collect();
  };

  onCampaignTracking = (campaignId, name) => {
    const events = this.event.data.campaign[name] || [];
    if (!events.includes(campaignId)) events.push(campaignId);
    this.event.data.campaign[name] = events;
    this.save();
  };

  onBasketAdd = () => {
    if (this.sessionInfo.context.categories && typeof (this.sessionInfo.context.categories) === 'string') {
      this.updatePartialMetrics(this.sessionInfo.context.categories, 'addToBasket');
    }
    if (this.sessionInfo.context.cart) {
      this.event.data.session.basketTotal = this.sessionInfo.context.cart.total;
    }
    this.event.data.session.addToBasket += 1;
    this.event.data.daily.addToBasket += 1;
    this.event.data.total.addToBasket += 1;

    this.onEvent(Constants.EVENT.ADD_TO_BASKET);
  };

  onEvent = () => {
    this.event.data.daily.lastSessionAction = Date.now();
    this.event.data.total.lastSessionAction = Date.now();
    this.save();
  };

  loadSession = () => {
    const sinceLastSession = this.event.data.total.lastSessionAction - this.event.data.session.startDate;
    let sinceLastDaily = this.event.data.total.lastSessionAction - this.event.data.daily.startDate;

    if (sinceLastDaily > 86400000) {
      sinceLastDaily = 0;
      this.event.data.resetDaily();
    }
    if (!this.event.data.session.startDate || sinceLastSession > 3600000) {
      this.onNewSession();
    }

    this.event.data.daily.sinceLastSession = sinceLastDaily;
    this.event.data.total.sinceLastSession = sinceLastSession;
    this.getLandingPageContext();
  };

  getLandingPageContext = () => {
    const store = sessionStorage.getItem(Constants.landingPageStorageKey);
    if (store && Utils.isStringJson(store)) {
      this.core.sessionInfo.context.landingPageContext = JSON.parse(store);
      return;
    }

    const context = { ...this.core.sessionInfo.context };
    context.pageView = this.event.data.total.pageView;

    const landingContext = {
      device: context.device,
      page_type: context.page_type,
      geoloc: context.geoloc,
      geoinfo: context.geoinfo,
      trafficSource: context.trafficSource,
      product: context.product,
      cart: context.cart,
      transaction: context.transaction,
      customer: context.customer,
      categories: context.categories,
      custom: context.custom,
      href: context.href,
      hostname: context.hostname,
      source: context.source,
      sponsorId: context.sponsorId,
      auth_status: context.auth_status,
    };
    if (Utils.isJsonStringify(landingContext)) {
      sessionStorage.setItem(Constants.landingPageStorageKey, JSON.stringify(landingContext));
      this.core.sessionInfo.context.landingPageContext = landingContext;
    } else {
      try {
        if (Math.random() * 100 > 95) {
          Utils.message('stringify error: landingPage', landingContext);
        }
      } catch (e) {
        Utils.message('stringify error: landingPage');
        console.log(e);
      }
    }
  };

  initSession = () => {
    this.event.data.resetSession();
  };

  onNewSession = () => {
    this.initSession();
    this.event.data.daily.session += 1;
    this.event.data.total.session += 1;
  };

  updatePartialMetrics = (categories, metric) => {
    const catlist = categories.split('|');
    if (!this.event.data.daily.partial) {
      this.event.data.daily.partial = {};
    }
    if (!this.event.data.session.partial) {
      this.event.data.session.partial = {};
    }

    for (let i = 0; i < catlist.length; i += 1) {
      const cat = catlist[i];
      if (cat) {
        if (!this.event.data.context.partialList) {
          this.event.data.context.partialList = [];
        }
        const p = this.event.data.context.partialList.find(partial => partial.category === cat);
        if (!p) {
          this.event.data.context.partialList.push({ category: cat });
          if (this.event.data.context.partialList.length > this.maxPartialLength) {
            const removed = this.event.data.context.partialList[0];
            delete (this.event.data.daily[`${removed.category}`]);
            this.event.data.context.partialList = this.event.data.context.partialList.splice(0, 1);
          }
        }

        if (!this.event.data.daily.partial[`${cat}`]) {
          this.event.data.daily.partial[`${cat}`] = { pageView: 0, addToBasket: 0 };
        }

        this.event.data.daily.partial[`${cat}`][metric] += 1;
      }
    }
  };

  pageTimeTick = () => {
    this.computeTimeValues();
    this.save();
  };

  computeTimeValues = () => {
  };

  collect = (force = false) => {
    if (!this.core.trackingConsented()) return;
    let context = this.event.context;
    if (!Utils.isJsonStringify(context)) {
      context = {};
      try {
        if (Math.random() * 100 > 95) {
          Utils.message('stringify error: collect', StringifyObject(this.event.context));
        }
      } catch (e) {
        Utils.message('stringify error: collect');
        console.log(e);
      }
    }

    const body = {
      id: this.event.id,
      context: JSON.stringify(context),
      geoloc: this.event.geoloc,
      data: JSON.stringify(this.event.data),
    };
    this.core.ciblerAPI.collect(body, force);
  }

  addCustomData = (key, value) => {
    if (Array.isArray(key) && key.length) {
      let i;
      let local;
      for (i = 0; i < key.length - 1; i += 1) {
        local = this.event.data[key[i]];
      }

      local[key[i]] = this.updateVal(local[key[i]], value);
      return;
    }

    this.event.data[key] = this.updateVal(this.event.data[key], value);
  }

  updateVal = (current, newValue) => {
    if (newValue) return newValue;
    if (current) return current+1;
    return 1;
  }
}
